import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Loader2, Send, CheckCircle2, QrCode, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API_BASE } from '@/utils/api';
import { errText } from './StepsBasic';

const ROLES = [['waiter', 'Mozo'], ['cashier', 'Caja'], ['kitchen', 'Cocina'], ['business_admin', 'Administrador']];

export const StepTeam = ({ onDone }) => {
  const [form, setForm] = useState({ email: '', name: '', role: 'waiter' });
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API_BASE}/onboarding/invites`).then(r => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/onboarding/invites`, { ...form, name: form.name || null });
      toast.success(data.email_sent ? `Invitación enviada a ${data.email}` : 'Invitación creada (no se pudo enviar el email: copiá el link)');
      if (!data.email_sent) navigator.clipboard?.writeText(data.activation_link)?.catch(() => {});
      setForm({ email: '', name: '', role: form.role });
      load();
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="step-team">
      <form onSubmit={invite} className="grid sm:grid-cols-[1fr_1fr_150px_auto] gap-2">
        <Input type="email" required placeholder="email@ejemplo.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" />
        <Input placeholder="Nombre (opcional)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="invite-name-input" />
        <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} data-testid="invite-role-select">
          {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <Button type="submit" disabled={busy} className="rounded-full" data-testid="invite-send-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Invitar</Button>
      </form>
      <p className="text-xs text-muted-foreground">Cada link de activación sirve una sola vez y vence en 72 horas.</p>
      <div className="space-y-2" data-testid="invite-list">
        {list.map(i => (
          <div key={i.invite_id} className="flex items-center justify-between text-sm border border-border rounded-xl px-3 py-2" data-testid={`invite-row-${i.invite_id}`}>
            <span>{i.email} · {ROLES.find(r => r[0] === i.role)?.[1]}</span>
            <span className={i.used_at ? 'text-green-700' : 'text-muted-foreground'}>{i.used_at ? 'Activada' : 'Pendiente'}</span>
          </div>
        ))}
      </div>
      <Button onClick={onDone} className="rounded-full" variant={list.length ? 'default' : 'outline'} data-testid="team-continue-btn">Seguir</Button>
    </div>
  );
};

export const StepTest = ({ onDone }) => {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [qrSrc, setQrSrc] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    const poll = () => axios.get(`${API_BASE}/onboarding/test`).then(r => {
      setInfo(r.data);
      if (r.data.order_received) clearInterval(timer.current);
    }).catch(() => {});
    poll();
    timer.current = setInterval(poll, 4000);
    return () => clearInterval(timer.current);
  }, []);

  useEffect(() => {
    if (!info?.unit?.unit_id || qrSrc) return;
    axios.get(`${API_BASE}/units/${info.unit.unit_id}/qr`, { responseType: 'blob' }).then(r => setQrSrc(URL.createObjectURL(r.data))).catch(() => {});
  }, [info, qrSrc]);

  if (!info) return <Loader2 className="w-6 h-6 animate-spin" />;
  if (!info.unit) return <p className="text-sm" data-testid="test-no-units">Primero creá al menos una mesa en el paso Mesas.</p>;
  const localUrl = `${window.location.origin}${info.unit.menu_path}`;
  return (
    <div className="grid md:grid-cols-[220px_1fr] gap-6 items-start" data-testid="step-test">
      <div className="bg-white rounded-3xl border border-border p-4 grid place-items-center">
        {qrSrc ? <img src={qrSrc} alt={`QR ${info.unit.name}`} className="w-48 h-48" data-testid="test-qr-image" /> : <QrCode className="w-24 h-24 text-muted-foreground" />}
        <p className="text-sm font-semibold mt-2">{info.unit.name}</p>
      </div>
      <div className="space-y-4">
        <ol className="text-sm space-y-1 list-decimal pl-4">
          <li>Escaneá el QR con tu celular (o abrí el link).</li>
          <li>Hacé un pedido de prueba.</li>
          <li>Esperá la confirmación acá: significa que llegó a cocina.</li>
        </ol>
        <div className="flex gap-2">
          <Input readOnly value={localUrl} data-testid="test-menu-url" />
          <Button variant="outline" size="icon" onClick={() => { navigator.clipboard?.writeText(localUrl)?.catch(() => {}); toast.success('Link copiado'); }} data-testid="test-copy-url"><Copy className="w-4 h-4" /></Button>
        </div>
        {info.order_received ? (
          <div className="rounded-2xl bg-green-50 border border-green-300 p-4 text-green-800 flex items-center gap-2" data-testid="test-order-received">
            <CheckCircle2 className="w-5 h-5" />¡Pedido recibido en cocina! Tu negocio ya está funcionando.
          </div>
        ) : (
          <div className="rounded-2xl bg-muted p-4 text-sm flex items-center gap-2" data-testid="test-waiting"><Loader2 className="w-4 h-4 animate-spin" />Esperando el primer pedido…</div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button className="rounded-full" onClick={() => navigate('/qr-center')} data-testid="go-qr-center-btn"><QrCode className="w-4 h-4 mr-1" />Ir al Centro de QRs</Button>
          <Button variant="outline" className="rounded-full" onClick={onDone} data-testid="test-finish-btn">Terminar</Button>
        </div>
      </div>
    </div>
  );
};
